import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Divider,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { formatDataBr } from '../../hooks/useTarefas';
import CompartilharDialog from './CompartilharDialog';

const CORES_TAG = ['#f44336', '#ff9800', '#fdd663', '#8ab4f8', '#81c995', '#ce93d8', '#90a4ae'];

export default function BlocoNotasView({
  notas,
  todasTags,
  notaAtivaId,
  setNotaAtivaId,
  busca,
  onBuscaChange,
  onCriar,
  onSalvar,
  onExcluir,
  onCriarTag,
  loading,
  podeCompartilhar,
  listarUsuarios,
  onCompartilhar,
  onAlterarCompartilhamento,
  onRemoverCompartilhamento,
  filtroCompartilhadas
}) {
  const notaAtiva = notas.find((n) => n.id === notaAtivaId) || notas[0];
  const souDono = notaAtiva?.souDono !== false;
  const podeEditar = souDono || notaAtiva?.minhaPermissao === 'editar';
  const [dialogShare, setDialogShare] = useState(false);

  const [titulo, setTitulo] = useState('');
  const [conteudo, setConteudo] = useState('');
  const [tagIds, setTagIds] = useState([]);
  const [dialogTag, setDialogTag] = useState(false);
  const [novaTagNome, setNovaTagNome] = useState('');
  const [novaTagCor, setNovaTagCor] = useState(CORES_TAG[3]);

  useEffect(() => {
    if (notaAtiva) {
      setTitulo(notaAtiva.titulo || '');
      setConteudo(notaAtiva.conteudo || '');
      setTagIds((notaAtiva.tags || []).map((t) => t.id));
    }
  }, [notaAtiva?.id, notaAtiva?.titulo, notaAtiva?.conteudo, notaAtiva?.tags]);

  const salvar = (patch) => {
    if (!notaAtiva || !podeEditar) return;
    const dadosPatch =
      patch && typeof patch === 'object' && !patch.nativeEvent && !patch.target ? patch : {};
    onSalvar(notaAtiva.id, { titulo, conteudo, tagIds, ...dadosPatch });
  };

  const toggleTag = (id) => {
    if (!souDono || !podeEditar) return;
    const next = tagIds.includes(id) ? tagIds.filter((x) => x !== id) : [...tagIds, id];
    setTagIds(next);
    if (notaAtiva) onSalvar(notaAtiva.id, { titulo, conteudo, tagIds: next });
  };

  const handleCriarTag = async () => {
    const nome = novaTagNome.trim();
    if (!nome) return;
    const id = await onCriarTag({ nome, cor: novaTagCor });
    if (id) {
      const next = [...tagIds, id];
      setTagIds(next);
      setNovaTagNome('');
      setDialogTag(false);
      if (notaAtiva) onSalvar(notaAtiva.id, { titulo, conteudo, tagIds: next });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 400 }}>
      <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar título, texto, #hashtag ou tema..."
          value={busca}
          onChange={(e) => onBuscaChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            width: 200,
            flexShrink: 0,
            borderRight: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box sx={{ p: 1.5 }}>
            {!filtroCompartilhadas && (
              <Button
                fullWidth
                startIcon={<AddIcon />}
                onClick={onCriar}
                sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
              >
                Nova nota
              </Button>
            )}
          </Box>
          <List dense sx={{ flex: 1, overflow: 'auto' }}>
            {notas.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1 }}>
                Nenhuma nota encontrada
              </Typography>
            ) : (
              notas.map((n) => (
                <ListItemButton
                  key={n.id}
                  selected={n.id === notaAtiva?.id}
                  onClick={() => setNotaAtivaId(n.id)}
                >
                  <ListItemText
                    primary={n.titulo || 'Sem título'}
                    secondary={
                      n.compartilhada && n.dono
                        ? `De: ${n.dono.nome} · ${formatDataBr(n.updatedAt || n.createdAt)}`
                        : formatDataBr(n.updatedAt || n.createdAt)
                    }
                    primaryTypographyProps={{ noWrap: true, fontSize: '0.85rem' }}
                    secondaryTypographyProps={{ fontSize: '0.7rem' }}
                  />
                </ListItemButton>
              ))
            )}
          </List>
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2, minWidth: 0 }}>
          {notaAtiva ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TextField
                  fullWidth
                  variant="standard"
                  placeholder="Título da nota"
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  onBlur={() => salvar()}
                  disabled={!podeEditar}
                  InputProps={{ sx: { fontSize: '1.1rem', fontWeight: 500 } }}
                />
                {souDono && podeCompartilhar && (
                  <IconButton size="small" onClick={() => setDialogShare(true)} title="Compartilhar">
                    <ShareOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
                {souDono && (
                  <IconButton size="small" onClick={() => onExcluir(notaAtiva.id)} sx={{ color: 'text.secondary' }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                {notaAtiva.compartilhada && notaAtiva.dono && (
                  <span>{`Compartilhada por ${notaAtiva.dono.nome} · `}</span>
                )}
                {notaAtiva.compartilhada && (
                  <span>{`Permissão: ${notaAtiva.minhaPermissao === 'editar' ? 'editar' : 'visualizar'} · `}</span>
                )}
                <span>Criada: {formatDataBr(notaAtiva.createdAt)}</span>
                {notaAtiva.updatedAt && (
                  <span>{` · Atualizada: ${formatDataBr(notaAtiva.updatedAt)}`}</span>
                )}
              </Typography>

              {souDono && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mb: 1.5 }}>
                  <LocalOfferOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  {todasTags.map((t) => (
                    <Chip
                      key={t.id}
                      size="small"
                      label={t.nome}
                      onClick={() => toggleTag(t.id)}
                      onDelete={() => toggleTag(t.id)}
                      sx={{
                        bgcolor: tagIds.includes(t.id) ? t.cor : 'transparent',
                        border: `1px solid ${t.cor}`,
                        color: tagIds.includes(t.id) ? '#202124' : t.cor,
                        fontWeight: tagIds.includes(t.id) ? 600 : 400
                      }}
                    />
                  ))}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setDialogTag(true)}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    Nova tag
                  </Button>
                </Box>
              )}

              {!souDono && (notaAtiva.tags || []).length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                  {(notaAtiva.tags || []).map((t) => (
                    <Chip key={t.id} size="small" label={t.nome} sx={{ bgcolor: t.cor, color: '#202124' }} />
                  ))}
                </Box>
              )}

              <Divider sx={{ mb: 2, borderColor: 'rgba(255,255,255,0.08)' }} />
              <TextField
                fullWidth
                multiline
                minRows={12}
                placeholder="Escreva rascunhos, textos e ideias... Use #hashtag para palavras-chave"
                value={conteudo}
                onChange={(e) => setConteudo(e.target.value)}
                onBlur={() => salvar()}
                disabled={!podeEditar}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: { fontSize: '0.95rem', lineHeight: 1.6, color: 'text.primary' }
                }}
                sx={{ flex: 1 }}
              />
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary" gutterBottom>
                Nenhuma nota selecionada
              </Typography>
              <Button startIcon={<AddIcon />} onClick={onCriar} sx={{ textTransform: 'none' }}>
                Criar primeira nota
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <Dialog open={dialogTag} onClose={() => setDialogTag(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nova tag</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nome da tag"
            value={novaTagNome}
            onChange={(e) => setNovaTagNome(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <Typography variant="caption" color="text.secondary">
            Cor
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            {CORES_TAG.map((c) => (
              <Box
                key={c}
                onClick={() => setNovaTagCor(c)}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: c,
                  cursor: 'pointer',
                  border: novaTagCor === c ? '2px solid #fff' : '2px solid transparent',
                  boxShadow: novaTagCor === c ? `0 0 0 2px ${c}` : 'none'
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogTag(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCriarTag}>
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {podeCompartilhar && notaAtiva && souDono && (
        <CompartilharDialog
          open={dialogShare}
          onClose={() => setDialogShare(false)}
          tituloItem={notaAtiva.titulo || 'Sem título'}
          tipo="nota"
          compartilhamentos={notaAtiva.compartilhamentos || []}
          listarUsuarios={listarUsuarios}
          onCompartilhar={(usuarioId, permissao) => onCompartilhar(notaAtiva.id, usuarioId, permissao)}
          onAlterarPermissao={(usuarioId, permissao) =>
            onAlterarCompartilhamento(notaAtiva.id, usuarioId, permissao)
          }
          onRemover={(usuarioId) => onRemoverCompartilhamento(notaAtiva.id, usuarioId)}
        />
      )}
    </Box>
  );
}
